import { useChat } from "@ai-sdk/react";
import { parseAgentPart } from "@lala/harness/protocol";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { DefaultChatTransport, type UIMessage } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError, chatFetch } from "../lib/api-client.ts";
import { errorMessage, HTTP } from "../lib/api-error.ts";
import { API_BASE, apiUrl } from "../lib/api-routes.ts";
import { analytics, lengthBucket } from "../lib/telemetry/analytics.ts";
import type { ChatSurfaceName } from "../lib/telemetry/events.ts";

import {
  addTotals,
  readTurnUsage,
  sumTurns,
  totalTokens,
  type TurnUsage,
  type UsageTotals,
} from "../usage/usage.ts";
import type {
  AgentMessage,
  AgentPart,
  AgentStatus,
  ConfirmationDecision,
  ConfirmationResult,
} from "./agent-event.ts";
import { startConversation } from "./attachments.ts";
import {
  loadConversation,
  namedTitle,
  useConversationTitle,
  type LoadedConversation,
} from "./conversations.ts";
import { collectOutcomes, countPendingDecisions } from "./decisions.ts";
import { clearDraft, restoreUnsent } from "./draft.ts";

/**
 * ===========================================================================
 * LA COSTURA (seccion 9.1)
 * ===========================================================================
 *
 * Este es el UNICO archivo del frontend que conoce el Vercel AI SDK. Envuelve
 * `useChat` y expone hacia arriba el vocabulario propio (`AgentMessage`,
 * `AgentPart`).
 *
 * A cambio de esta traduccion nos quedamos gratis con el ensamblado del
 * stream, el estado de la conversacion, el aborto, los reintentos y la maquina
 * de estados de las llamadas a herramienta. Y si algun dia el SDK estorba, se
 * reescribe este archivo y ningun componente se entera.
 */

/**
 * Data parts del stream. Cada una, menos `conversation`, es una parte de
 * `@lala/harness/protocol` (seccion 9 de la auditoria del harness): se
 * decodifica y se valida con `parseAgentPart` en `toAgentMessage`, así que
 * aquí basta con `unknown` -- tiparlas a mano otra vez sería un segundo
 * decodificador.
 *
 * `conversation` es la EXCEPCIÓN: una señal propia de esta aplicación, ajena
 * al protocolo, que dice el id de la conversación en cuanto se sabe --antes
 * de que el turno termine-- para que recargar a mitad de un turno no pierda
 * el hilo (ver `onData` más abajo).
 *
 * Tiene que ser un alias de tipo, no una interface: las interfaces no reciben
 * indice implicito, asi que no satisfacen `UIDataTypes` (= Record<string,
 * unknown>) y el tipo de `data` colapsaria a `unknown`.
 */
type AgentDataParts = {
  conversation: { conversationId: string };
  tool: unknown;
  ui_component: unknown;
  resource_link: unknown;
  confirmation_request: unknown;
  confirmation_result: unknown;
  question_request: unknown;
  question_answer: unknown;
  file: unknown;
  error: unknown;
  presentation: unknown;
  finish: unknown;
};

type AgentUIMessage = UIMessage<unknown, AgentDataParts>;

/**
 * De dónde y cómo salió un mensaje, para la telemetría (`chat_message_sent`):
 * superficie, origen, dictado o tecleado, los adjuntos y el texto tal cual
 * lo escribió la persona.
 */
export interface SendMeta {
  readonly surface?: ChatSurfaceName;
  readonly origin?: "composer" | "alert" | "mechanic";
  readonly inputMethod?: "typed" | "voice";
  readonly attachmentCount?: number;
  readonly attachmentNames?: readonly string[];
  /** El largo de lo que escribió la persona, sin el prefacio de adjuntos. */
  readonly length?: number;
  /** Lo que escribió la persona, sin el prefacio de adjuntos. */
  readonly text?: string;
}

/** El turno en marcha, para medirlo: cuándo empezó y qué lo arrancó. */
interface TurnClock {
  readonly startedAt: number;
  readonly kind: "message" | "confirmation" | "answer";
  firstByteAt?: number;
}

export interface UseAgentChatResult {
  readonly messages: readonly AgentMessage[];
  readonly status: AgentStatus;
  readonly error: Error | undefined;
  readonly conversationId: string | undefined;
  /**
   * Cómo llamar a esta conversación, o `null` si todavía no tiene nombre.
   *
   * `null` no es un error ni un hueco que haya que rellenar: es el estado
   * normal de una conversación recién empezada, porque quien la nombra es un
   * modelo en el backend y tarda un momento en hacerlo. Quien lo pinta tiene
   * que saber DECIR que aún no hay título (ver `UNTITLED_CONVERSATION`).
   */
  readonly title: string | null;
  /** Los turnos antiguos que el agente ya solo ve resumidos, si los hay. */
  readonly compacted:
    { readonly count: number; readonly summary: string } | undefined;
  /** Lo que lleva gastado la conversación abierta, si ha gastado algo. */
  readonly usage: UsageTotals | undefined;
  /** Se está recuperando una conversación del historial. */
  readonly loadingHistory: boolean;
  /**
   * Por qué no se pudo abrir la conversación de la URL, si no se pudo.
   *
   * Antes se tragaba y el hilo quedaba en blanco: una conversación ajena --un
   * 403-- o borrada --un 404-- se leía igual que una vacía.
   */
  readonly historyError: Error | undefined;
  /**
   * Tarjetas que siguen esperando una decisión en esta conversación.
   *
   * El bucle del agente está PARADO hasta que se contesten (§8.2), así que
   * esto no es un adorno: es lo que permite avisar cuando la ventana del chat
   * está cerrada y alguien tiene que decidir.
   */
  readonly pendingDecisions: number;
  /**
   * Qué confirmaciones y preguntas se han contestado YA en esta sesión.
   *
   * Hace falta aquí arriba y no dentro de cada tarjeta: la tarjeta se
   * desmonta al cerrar la ventana del chat o al cambiar de modo, y al volver
   * a montarse ofrecería otra vez unos botones que ya no valen. El backend
   * solo cuenta lo que pasó (`settled`) al releer la conversación.
   */
  readonly answered: ReadonlySet<string>;
  /** Cómo se decidió cada confirmación de esta sesión. */
  readonly decisions: ReadonlyMap<string, ConfirmationDecision>;
  /**
   * Cómo acabó cada tarjeta según el backend (R2.1), por su `actionId`.
   * Sale del historial y del stream: es lo que dice "7 de 8 hechas".
   */
  readonly outcomes: ReadonlyMap<string, ConfirmationResult>;
  send: (text: string, meta?: SendMeta) => void;
  /**
   * Empieza una conversación NUEVA con esta pregunta, en una sola operación.
   *
   * No es `startNew()` y luego `send()`: `send` es un `useCallback` cerrado
   * sobre el `conversationId` de este render, así que encadenarlos en el mismo
   * tick mandaría el mensaje a la conversación ANTERIOR --y encima sin que se
   * note, porque en pantalla todo parece correcto--. Aquí el cuerpo va vacío a
   * propósito: sin `conversationId`, el backend crea una.
   */
  askInNewConversation: (text: string, meta?: SendMeta) => void;
  /** Responde a una tarjeta de confirmación y continúa la conversación. */
  resolveConfirmation: (
    actionId: string,
    decision: ConfirmationDecision,
    surface?: ChatSurfaceName,
  ) => void;
  answerQuestions: (
    questionId: string,
    answers: readonly {
      key: string;
      selected: readonly string[];
      custom?: string;
    }[],
    note?: string,
    surface?: ChatSurfaceName,
  ) => void;
  stop: () => void;
  clearError: () => void;
  /** Crea la conversación si aún no existe. La necesitan los adjuntos. */
  ensureConversation: () => Promise<string>;
  /** Abre una conversación del historial. */
  open: (conversationId: string) => void;
  /** Empieza de cero. La anterior no se pierde: está en el historial. */
  startNew: () => void;
  /**
   * Cuántas veces ha cambiado el usuario de conversación: `open`, `startNew`
   * o `askInNewConversation`. Es la `key` del compositor, que a cada cambio se
   * monta de nuevo, vacío y con el foco dentro.
   *
   * No es el `conversationId`: ese también cambia cuando la conversación nueva
   * recibe su id --al primer mensaje o al adjuntar--, y ahí la persona sigue
   * en el mismo sitio y puede tener algo escrito.
   */
  readonly switches: number;
}

/**
 * La conversación abierta viaja en la URL, en el parámetro `c`.
 *
 * Es lo que hace que recargar no pierda el hilo y que se pueda compartir un
 * enlace a un chat. Lo escribe el router --que además valida que sea un uuid--
 * en vez de manejarse con `history.replaceState`.
 */

export const useAgentChat = (): UseAgentChatResult => {
  const navigate = useNavigate();
  /**
   * La conversación abierta la manda la URL, no un estado paralelo.
   *
   * Con dos fuentes --una en la URL y otra en el componente-- tarde o temprano
   * se separan, y entonces el botón de atrás del navegador enseña una cosa y
   * la aplicación otra.
   */
  const conversationId = useSearch({
    from: "__root__",
    select: (search) => search.c,
  });

  const setConversationId = useCallback(
    (id: string | undefined) => {
      void navigate({
        to: ".",
        search: (previous) => ({ ...previous, c: id }),
        replace: true,
      });
    },
    [navigate],
  );
  /**
   * Lo recuperado del historial, aparte de lo que lleva el SDK.
   *
   * No se le inyecta al SDK: lo suyo es el turno en curso. Mezclarlos aquí,
   * al leer, evita traducir nuestro vocabulario al suyo para volver a
   * traducirlo de vuelta en cuanto se dibuja.
   */
  const [history, setHistory] = useState<LoadedConversation | undefined>();
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<Error | undefined>();
  /** Ids de confirmaciones y preguntas contestadas en esta sesión. */
  const [answered, setAnswered] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  /**
   * Qué se decidió en cada confirmación de esta sesión: aprobar o rechazar.
   *
   * `answered` dice que ya no espera; esto dice cómo acabó, y lo necesitan
   * las llamadas a herramienta del mismo mensaje para dejar de decir "espera
   * tu confirmación" y decir lo que pasó. Al recargar se pierde: el backend
   * solo cuenta si sigue pendiente.
   */
  const [decisions, setDecisions] = useState<
    ReadonlyMap<string, ConfirmationDecision>
  >(() => new Map());

  const markAnswered = useCallback((id: string) => {
    setAnswered((current) => new Set(current).add(id));
  }, []);

  const transport = useMemo(
    () =>
      new DefaultChatTransport<AgentUIMessage>({
        api: apiUrl(`${API_BASE}/chat`),
        fetch: chatFetch,
        // El backend espera { text, conversationId }, no el array de mensajes
        // del SDK: el historial ya vive en Postgres y se carga alli, que es la
        // unica fuente de verdad de la conversacion.
        prepareSendMessagesRequest: ({ messages, body }) => {
          const last = messages.at(-1);
          const text =
            last?.parts
              .filter((part) => part.type === "text")
              .map((part) => part.text)
              .join("") ?? "";

          // Ni una confirmación ni unas respuestas llevan texto: van en el
          // body y el backend las reconoce por ahí.
          const sent = body as
            { resolve?: unknown; answer?: unknown } | undefined;
          const isReply =
            sent?.resolve !== undefined || sent?.answer !== undefined;
          return { body: isReply ? { ...body } : { ...body, text } };
        },
      }),
    [],
  );

  /**
   * Lo último que se mandó, por si hay que devolverlo a la caja.
   *
   * Si la sesión caduca y no se puede renovar, el turno falla con un 401 y la
   * aplicación vuelve al SSO. El mensaje ya no está en el compositor --se
   * vació al enviar-- y sin esto se perdería en el viaje. El id va con él:
   * el borrador es por conversación, así que hay que devolverlo a la misma
   * de la que salió, no a la que esté abierta cuando llegue el error.
   */
  const lastSent = useRef("");
  const lastSentConversation = useRef<string | undefined>(undefined);

  /**
   * Lo que la telemetría necesita y no es estado de React: el turno en marcha,
   * la conversación en la que acabó --su id llega a mitad del stream-- y
   * cuándo se vio cada tarjeta, para medir cuánto se tardó en contestarla.
   */
  const turn = useRef<TurnClock | null>(null);
  const liveConversation = useRef(conversationId);
  liveConversation.current = conversationId ?? liveConversation.current;
  const seenAt = useRef(new Map<string, number>());

  const startTurn = (kind: TurnClock["kind"]) => {
    turn.current = { startedAt: Date.now(), kind };
  };

  const chat = useChat<AgentUIMessage>({
    transport,
    onError: (error) => {
      if (error instanceof ApiError && error.status === HTTP.unauthorized) {
        restoreUnsent(lastSentConversation.current, lastSent.current);
      }
      analytics.chat.turnFailed({
        conversationId: liveConversation.current,
        ...(error instanceof ApiError
          ? {
              status: error.status,
              code: error.code,
              ...(error.errorId === undefined
                ? {}
                : { errorId: error.errorId }),
            }
          : {}),
        reason: errorMessage(error),
      });
    },
    onFinish: ({ message, isAbort, isError, finishReason }) => {
      const clock = turn.current;
      turn.current = null;
      if (clock === null) return;
      const reply = toAgentMessage(message);
      analytics.chat.turnCompleted({
        conversationId: liveConversation.current,
        kind: clock.kind,
        durationMs: Date.now() - clock.startedAt,
        ...(clock.firstByteAt === undefined
          ? {}
          : { ttfbMs: clock.firstByteAt - clock.startedAt }),
        ...turnShape(reply),
        isAbort,
        isError,
        ...(finishReason === undefined ? {} : { finishReason }),
      });
    },
    // Sin esto, cada fragmento del stream provoca un render del hilo entero.
    // 60 ms es imperceptible al leer y corta la mayor parte del trabajo.
    throttle: 60,
    onData: (part) => {
      // El id de la conversacion llega al inicio del primer turno. Se escribe
      // en la URL en cuanto se sabe: si el usuario recarga a mitad del turno,
      // vuelve a este chat y no a uno en blanco.
      if (part.type === "data-conversation") {
        liveConversation.current = part.data.conversationId;
        setConversationId(part.data.conversationId);
      }
      if (part.type === "data-confirmation_result") {
        const result = parseAgentPart(part.data);
        if (result === null || result.type !== "confirmation_result") return;
        analytics.chat.confirmationResult({
          conversationId: liveConversation.current,
          actionId: result.actionId,
          outcome: result.outcome,
          doneCount: result.results.filter((r) => r.status === "done").length,
          failedCount: result.results.filter((r) => r.status === "failed")
            .length,
          errors: result.results.flatMap((r) =>
            r.error === null ? [] : [r.error],
          ),
        });
      }
    },
  });

  /** Los mensajes de este render, para lo que se lee desde un callback. */
  const current = useRef<readonly AgentMessage[]>([]);

  const trackSent = (
    clean: string,
    meta: SendMeta | undefined,
    newConversation: boolean,
  ) => {
    analytics.chat.messageSent({
      conversationId: newConversation ? undefined : liveConversation.current,
      isNewConversation: newConversation,
      turnIndex: newConversation
        ? 0
        : current.current.filter((m) => m.role === "user").length,
      surface: meta?.surface ?? "chat",
      origin: meta?.origin ?? "composer",
      inputMethod: meta?.inputMethod ?? "typed",
      attachmentCount: meta?.attachmentCount ?? 0,
      ...(meta?.attachmentNames === undefined
        ? {}
        : { attachmentNames: meta.attachmentNames }),
      lengthBucket: lengthBucket(meta?.length ?? clean.length),
      text: meta?.text ?? clean,
    });
  };

  const send = useCallback(
    (text: string, meta?: SendMeta) => {
      const clean = text.trim();
      if (clean === "") return;
      lastSent.current = clean;
      lastSentConversation.current = conversationId;
      trackSent(clean, meta, conversationId === undefined);
      startTurn("message");
      void chat.sendMessage(
        { text: clean },
        { body: conversationId === undefined ? {} : { conversationId } },
      );
    },
    [chat, conversationId],
  );

  const resolveConfirmation = useCallback(
    (
      actionId: string,
      decision: ConfirmationDecision,
      surface?: ChatSurfaceName,
    ) => {
      const request = findConfirmation(current.current, actionId);
      const items = request?.items ?? [];
      const toolNames = [...new Set(items.map((item) => item.toolName))];
      const seen = seenAt.current.get(actionId);
      analytics.chat.confirmationDecided({
        conversationId: liveConversation.current,
        actionId,
        decision,
        ...(request === undefined ? {} : { toolName: request.toolName }),
        ...(toolNames.length > 0 ? { toolNames } : {}),
        itemCount: Math.max(items.length, 1),
        grouped: items.length > 0,
        ...(seen === undefined ? {} : { decisionLatencyMs: Date.now() - seen }),
        surface: surface ?? "chat",
        ...(request === undefined ? {} : { summary: request.summary }),
      });
      startTurn("confirmation");
      markAnswered(actionId);
      setDecisions((current) => new Map(current).set(actionId, decision));
      void chat.sendMessage(
        // Sin texto: lo que el usuario "dice" es su decisión, y el backend la
        // convierte en el mensaje que el modelo leerá.
        { text: "" },
        {
          body: {
            resolve: { actionId, decision },
            ...(conversationId === undefined ? {} : { conversationId }),
          },
        },
      );
    },
    [chat, conversationId, markAnswered],
  );

  const answerQuestions = useCallback(
    (
      questionId: string,
      answers: readonly {
        key: string;
        selected: readonly string[];
        custom?: string;
      }[],
      note?: string,
      surface?: ChatSurfaceName,
    ) => {
      const seen = seenAt.current.get(questionId);
      const asked = findQuestions(current.current, questionId);
      analytics.chat.questionsAnswered({
        conversationId: liveConversation.current,
        questionCount: answers.length,
        customCount: answers.filter(
          (answer) =>
            answer.custom !== undefined && answer.custom.trim() !== "",
        ).length,
        hasNote: note !== undefined && note.trim() !== "",
        ...(seen === undefined ? {} : { latencyMs: Date.now() - seen }),
        surface: surface ?? "chat",
        prompt: asked?.prompt ?? null,
        answers: answers.map((answer) => ({
          question:
            asked?.questions.find((q) => q.key === answer.key)?.text ??
            answer.key,
          selected: answer.selected,
          ...(answer.custom === undefined ? {} : { custom: answer.custom }),
        })),
        ...(note === undefined || note.trim() === ""
          ? {}
          : { note: note.trim() }),
      });
      startTurn("answer");
      markAnswered(questionId);
      void chat.sendMessage(
        { text: "" },
        {
          body: {
            answer: {
              questionId,
              answers,
              ...(note !== undefined && note.trim() !== ""
                ? { note: note.trim() }
                : {}),
            },
            ...(conversationId === undefined ? {} : { conversationId }),
          },
        },
      );
    },
    [chat, conversationId, markAnswered],
  );

  const { setMessages } = chat;

  const [switches, setSwitches] = useState(0);

  /**
   * Lo que pasa cada vez que el usuario deja una conversación por otra: el
   * compositor se remonta para leer el borrador de LA OTRA, que es un hueco
   * aparte (`draft.ts`) y no se toca aquí --si tenía algo a medio escribir,
   * sigue ahí cuando se vuelva.
   *
   * NO lo hace la carga al abrir con `?c=` en la URL: al volver del SSO por un
   * 401, lo que hay en el borrador es justo el mensaje que no llegó a salir.
   */
  const switchConversation = useCallback(() => {
    setSwitches((count) => count + 1);
  }, []);

  const load = useCallback(
    (id: string, origin: "history" | "url") => {
      liveConversation.current = id;
      setLoadingHistory(true);
      setHistoryError(undefined);
      setConversationId(id);

      setMessages([]);
      setAnswered(new Set());
      setDecisions(new Map());

      loadConversation(id)
        .then((loaded) => {
          analytics.chat.conversationOpened({
            origin,
            conversationId: id,
            messageCount: loaded.messages.length,
            title: namedTitle(loaded.title),
          });
          setHistory(loaded);
        })
        .catch((cause: unknown) => {
          analytics.chat.conversationOpenFailed({
            conversationId: id,
            reason: errorMessage(cause),
            ...(cause instanceof ApiError
              ? { status: cause.status, code: cause.code }
              : {}),
          });
          // Una conversación que no se puede abrir no bloquea el chat: se
          // deja en blanco y el usuario puede escribir igual. Pero se dice
          // por qué, en vez de hacer pasar el fallo por un hilo vacío.
          setHistory(undefined);
          setHistoryError(
            cause instanceof Error ? cause : new Error(String(cause)),
          );
        })
        .finally(() => {
          setLoadingHistory(false);
        });
    },
    [setConversationId, setMessages],
  );

  const open = useCallback(
    (id: string) => {
      switchConversation();
      load(id, "history");
    },
    [load, switchConversation],
  );

  const startNew = useCallback(() => {
    liveConversation.current = undefined;
    switchConversation();
    // A diferencia de abrir otra conversación --que conserva su propio
    // borrador--, "nueva conversación" es un pedido explícito de empezar en
    // blanco: tira lo que hubiera quedado en el hueco de la conversación sin
    // id, aunque sea el mismo hueco del que se viene.
    clearDraft(undefined);
    setHistoryError(undefined);
    setConversationId(undefined);
    setHistory(undefined);
    setMessages([]);
    setAnswered(new Set());
    setDecisions(new Map());
  }, [setConversationId, setMessages, switchConversation]);

  /**
   * Preguntar algo en una conversación nueva, de una sola vez.
   *
   * Lo usan las alertas del Resumen: cada una es una investigación suya
   * --"¿quiénes son los 18 que se quedaron fuera?"-- y colarla en un hilo que
   * iba de otra cosa ensucia el contexto que ve el modelo, el título que le
   * pone el backend y lo que la extracción de memoria (§11) saque después.
   *
   * Lo importante es que el envío va en la MISMA llamada que la limpieza, con
   * el cuerpo vacío. Partirlo en `startNew()` + `send()` colgaría el mensaje
   * de la conversación anterior, porque `send` lleva dentro el id de este
   * render.
   */
  const askInNewConversation = useCallback(
    (text: string, meta?: SendMeta) => {
      const clean = text.trim();
      if (clean === "") return;
      lastSent.current = clean;
      lastSentConversation.current = undefined;
      if (meta?.origin === "alert") {
        analytics.chat.conversationNew({ origin: "alert" });
      }
      trackSent(clean, meta, true);
      startTurn("message");
      liveConversation.current = undefined;

      switchConversation();
      clearDraft(undefined);
      setConversationId(undefined);
      setHistory(undefined);
      setHistoryError(undefined);
      setMessages([]);
      setAnswered(new Set());
      setDecisions(new Map());

      // Sin `conversationId` en el cuerpo: es lo que le dice al backend que
      // cree una. El id de la nueva llega por el stream y se escribe en la URL.
      void chat.sendMessage({ text: clean }, { body: {} });
    },
    [chat, setConversationId, setMessages, switchConversation],
  );

  // Al abrir con ?c=<id>, se recupera esa conversación. Es lo que hace que
  // recargar a mitad de un chat no lo pierda.
  useEffect(() => {
    if (conversationId !== undefined) load(conversationId, "url");
    // Solo al montar: después, quien cambia la URL es `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ensureConversation = useCallback(async (): Promise<string> => {
    if (conversationId !== undefined) return conversationId;
    const created = await startConversation();
    setConversationId(created);
    return created;
  }, [conversationId, setConversationId]);

  const live = useMemo(
    () => chat.messages.map(toAgentMessage),
    [chat.messages],
  );

  const messages = useMemo(
    () => [...(history?.messages ?? []), ...live],
    [history, live],
  );
  current.current = messages;

  // Cuándo se vio cada tarjeta por primera vez: de ahí sale cuánto se tardó
  // en decidir. Las del historial cuentan desde que se abrió.
  useEffect(() => {
    for (const message of messages) {
      for (const part of message.parts) {
        const id =
          part.type === "confirmation_request"
            ? part.actionId
            : part.type === "question_request"
              ? part.questionId
              : undefined;
        if (id !== undefined && !seenAt.current.has(id)) {
          seenAt.current.set(id, Date.now());
        }
      }
    }
  }, [messages]);

  // El primer byte del turno: cuando deja de esperar y empieza a llegar.
  useEffect(() => {
    if (chat.status === "streaming" && turn.current !== null) {
      turn.current.firstByteAt ??= Date.now();
    }
  }, [chat.status]);

  /**
   * Lo que lleva gastado la conversación abierta.
   *
   * Lo recuperado lo dice el backend --y cuenta también lo que corre en el
   * worker para esta conversación: títulos, compactación, memoria--; lo de
   * esta sesión se suma turno a turno según llega, sin volver a pedir la
   * conversación entera por un número. Al recargar vuelve a mandar el backend.
   */
  const usage = useMemo(() => {
    const recovered =
      history?.usage ?? sumTurns((history?.messages ?? []).map((m) => m.usage));
    const total = addTotals(recovered, sumTurns(live.map((m) => m.usage)));
    return totalTokens(total) === 0 ? undefined : total;
  }, [history, live]);

  /**
   * El título sale del historial, no del hilo que se está leyendo.
   *
   * Aquí hubo una segunda vía: recortar el primer mensaje del usuario para que
   * la cabecera no estuviera vacía durante el primer turno, porque el backend
   * derivaba el título al guardar y no llegaba por el stream. Ya no hace
   * falta: ahora lo escribe un modelo, y un recorte puesto por delante taparía
   * justo el momento que se quiere enseñar --el de "sin título" convirtiéndose
   * en el de verdad--.
   *
   * La lista manda sobre lo cargado al abrir: `history` se lee una sola vez y
   * se queda con el título que hubiera entonces, que en una conversación nueva
   * es ninguno.
   */
  const listed = useConversationTitle(conversationId);
  const title = listed ?? namedTitle(history?.title);

  const outcomes = useMemo(() => collectOutcomes(messages), [messages]);

  /** La regla vive en `decisions.ts`, que se puede probar sin montar un chat. */
  const pendingDecisions = useMemo(
    () => countPendingDecisions(messages, answered),
    [messages, answered],
  );

  const compacted = useMemo(
    () =>
      history?.summary === null || history?.summary === undefined
        ? undefined
        : { count: history.compactedCount, summary: history.summary },
    [history],
  );

  return {
    messages,
    status: toAgentStatus(chat.status),
    error: chat.error,
    conversationId,
    title,
    compacted,
    usage,
    loadingHistory,
    historyError,
    pendingDecisions,
    answered,
    decisions,
    outcomes,
    send,
    askInNewConversation,
    resolveConfirmation,
    answerQuestions,
    // `stop` del SDK devuelve una promesa; se envuelve para que los
    // componentes reciban un handler sincrono y no tengan que saberlo.
    stop: () => {
      const clock = turn.current;
      analytics.chat.turnStopped({
        conversationId: liveConversation.current,
        ...(clock === null ? {} : { elapsedMs: Date.now() - clock.startedAt }),
      });
      void chat.stop();
    },
    clearError: () => {
      analytics.chat.errorDismissed();
      chat.clearError();
    },
    ensureConversation,
    open,
    startNew,
    switches,
  };
};

/** La tarjeta de confirmación con ese id, esté en el historial o en vivo. */
const findConfirmation = (
  messages: readonly AgentMessage[],
  actionId: string,
): Extract<AgentPart, { type: "confirmation_request" }> | undefined => {
  for (const message of messages) {
    for (const part of message.parts) {
      if (part.type === "confirmation_request" && part.actionId === actionId) {
        return part;
      }
    }
  }
  return undefined;
};

/**
 * Qué hizo el agente en un turno, sin su contenido: qué herramientas llamó,
 * cuántas fallaron, qué componentes dibujó y si dejó algo pendiente.
 */
export const turnShape = (message: AgentMessage) => {
  const tools = message.parts.filter((part) => part.type === "tool");
  const components = message.parts.flatMap((part) =>
    part.type === "ui_component" ? [part.render] : [],
  );
  return {
    toolNames: [...new Set(tools.map((tool) => tool.toolName))],
    toolErrorCount: tools.filter((tool) => tool.state === "error").length,
    components: [...new Set(components)],
    hasConfirmation: message.parts.some(
      (part) => part.type === "confirmation_request",
    ),
    hasQuestion: message.parts.some((part) => part.type === "question_request"),
    fileCount: message.parts.filter((part) => part.type === "file").length,
    responseText: message.parts
      .flatMap((part) => (part.type === "text" ? [part.text] : []))
      .join(""),
    questions: message.parts.flatMap((part) =>
      part.type === "question_request"
        ? part.questions.map((question) => question.text)
        : [],
    ),
  };
};

/** Las preguntas del agente con ese id, esté en el historial o en vivo. */
const findQuestions = (
  messages: readonly AgentMessage[],
  questionId: string,
): Extract<AgentPart, { type: "question_request" }> | undefined => {
  for (const message of messages) {
    for (const part of message.parts) {
      if (part.type === "question_request" && part.questionId === questionId) {
        return part;
      }
    }
  }
  return undefined;
};

const toAgentStatus = (status: string): AgentStatus => {
  switch (status) {
    case "submitted":
      return "submitted";
    case "streaming":
      return "streaming";
    case "error":
      return "error";
    default:
      return "idle";
  }
};

/**
 * Traduccion de una UIMessage del SDK a nuestro vocabulario.
 *
 * Texto y razonamiento son las partes NATIVAS del AI SDK (el escritor del
 * harness las emite así para la animación de escritura gratis, ver
 * `ui-message-writer.ts`). Todo lo demás --herramienta, confirmación,
 * pregunta, presentación-- viaja como *data part* (`data-<tipo>`) con la
 * parte del protocolo tal cual en `data`; se decodifica con `parseAgentPart`,
 * la única fuente de verdad del contrato, y una que no encaje se descarta en
 * vez de romper el hilo entero -- es lo que pide el contrato al declarar que
 * puede devolver `null`.
 */
const toAgentMessage = (message: AgentUIMessage): AgentMessage => {
  const parts: AgentPart[] = [];
  const results: ConfirmationResult[] = [];
  let usage: TurnUsage | undefined;

  for (const part of message.parts) {
    if (part.type === "text") {
      // Aprobar o contestar se envía sin texto: lo que "dice" el usuario es
      // su decisión, y ya la enseña la tarjeta. Un texto vacío no es nada.
      if (part.text !== "") parts.push({ type: "text", text: part.text });
      continue;
    }

    if (part.type === "reasoning") {
      parts.push({ type: "reasoning", text: part.text });
      continue;
    }

    if (!part.type.startsWith("data-")) continue;
    // `data-conversation` es una señal de control de esta aplicación, ajena
    // al protocolo: no se dibuja, y `onData` ya hizo lo que había que hacer.
    if (part.type === "data-conversation") continue;

    const decoded = parseAgentPart((part as { data: unknown }).data);
    if (decoded === null) continue;

    // Cómo acabó una tarjeta (R2.1): llega al empezar el turno que arranca
    // la decisión. No se dibuja aquí; lo leen la tarjeta y sus llamadas.
    if (decoded.type === "confirmation_result") {
      results.push(decoded);
      continue;
    }

    // El fin del turno no se dibuja como una parte: trae lo que costó, y eso
    // va al pie del mensaje.
    if (decoded.type === "finish") {
      usage = readTurnUsage(decoded.usage);
      continue;
    }

    parts.push(decoded);
  }

  return {
    id: message.id,
    role: message.role === "system" ? "system" : message.role,
    parts,
    ...(usage !== undefined ? { usage } : {}),
    ...(results.length > 0 ? { confirmationResults: results } : {}),
  };
};
